import {compact} from '@benchling/js-utils';
import {Draft} from 'immer';
import {Connection} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS, ActionOrigin} from 'constants/AnalyticsConstants';
import {findAllDownstreamMappableNodeIds} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';

import {isNewEdgeDuplicate} from '../edgeUtils';
import {getFieldInfoFromDataFlowEdgeHandle, makeDataFlowEdgeHandle} from '../flowchartIdentifierUtils';
import {
  FlowchartEdge,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from '../processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData, isStaticTaskSchemaNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChange} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {getTaskSchemaNodePairsForEdge} from '../types/transition/transitionLifecycleUtils';

/**
 * Finds nodes that have the same PTDV ID as the node with the given ID.
 */
export function findNodesWithMatchingPTDV(
  nodes: Array<StaticFlowchartNodeData>,
  nodeId: string
): Array<string> {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return [];
  }
  if (isStaticTaskSchemaNodeData(node)) {
    const taskDefinitionVersionId = node.taskDefinitionVersionSpec.clientId;
    const nodeDetailsType = node.nodeDetailsType;
    return nodes
      .filter(
        (n) =>
          isStaticTaskSchemaNodeData(n) &&
          n.taskDefinitionVersionSpec.clientId === taskDefinitionVersionId &&
          n.nodeDetailsType === nodeDetailsType
      )
      .map((n) => n.id);
  }
  return [];
}

/**
 * Gets edges of type @edgeType that are connected by the same PTDV IDs as the given source and target node IDs.
 */
export function extractMatchingEdges(
  edges: Array<FlowchartStaticEdgeData>,
  sourceNodeId: string,
  targetNodeId: string,
  nodes: Array<StaticFlowchartNodeData>,
  edgeType: ProcessFlowchartEdgeType,
  excludeOriginalPair: boolean = true
): Array<FlowchartStaticEdgeData> {
  const sourceNodeIds = findNodesWithMatchingPTDV(nodes, sourceNodeId);
  const targetNodeIds = findNodesWithMatchingPTDV(nodes, targetNodeId);

  const matchingEdges = edges.filter((edge) => {
    const matchesType = edge.type === edgeType;
    const matchesSource = sourceNodeIds.includes(edge.source);
    const matchesTarget = targetNodeIds.includes(edge.target);
    const isOriginalPair = edge.source === sourceNodeId && edge.target === targetNodeId;

    return matchesType && matchesSource && matchesTarget && (!excludeOriginalPair || !isOriginalPair);
  });

  return matchingEdges;
}

/**
 * Gets node pairs with PTDV IDs that match the given source and target node IDs and are connected by control flow edges
 * (these control flow edges may or may not go through router nodes)
 */
export function extractMatchingNodePairs(
  edges: Array<FlowchartStaticEdgeData>,
  sourceNodeId: string,
  targetNodeId: string,
  nodes: Array<StaticFlowchartNodeData>,
  excludeOriginalPair: boolean = true
): Array<{sourceNodeId: string; targetNodeId: string}> {
  const matchingSourceNodeIds = findNodesWithMatchingPTDV(nodes, sourceNodeId);
  const matchingTargetNodeIds = findNodesWithMatchingPTDV(nodes, targetNodeId);
  const nodePairs: Array<{sourceNodeId: string; targetNodeId: string}> = [];
  const nodesWithData = nodes.map((node) => ({id: node.id, data: node}));
  for (const matchingSourceNodeId of matchingSourceNodeIds) {
    const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(
      {nodes: nodesWithData, edges},
      matchingSourceNodeId
    );
    for (const downstreamMappableNodeId of downstreamMappableNodeIds) {
      const isOriginalPair =
        matchingSourceNodeId === sourceNodeId && downstreamMappableNodeId === targetNodeId;
      if (
        matchingTargetNodeIds.includes(downstreamMappableNodeId) &&
        (!excludeOriginalPair || !isOriginalPair)
      ) {
        nodePairs.push({sourceNodeId: matchingSourceNodeId, targetNodeId: downstreamMappableNodeId});
      }
    }
  }
  return nodePairs;
}

/**
 * Finds data flow edges between nodes that have the same PTDV IDs as @edge and are connected by control flow edges
 */
export function findMirroredEdges(
  edge: FlowchartStaticEdgeData,
  sourceNodeId: string,
  targetNodeId: string,
  sourceHandle: string,
  targetHandle: string,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>
): Array<FlowchartStaticEdgeData> {
  const nodePairsMatchingDataFlowEdge = extractMatchingNodePairs(edges, sourceNodeId, targetNodeId, nodes);

  const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(sourceHandle);
  const targetInfo = getFieldInfoFromDataFlowEdgeHandle(targetHandle);

  return edges.filter((edge) => {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
      return false;
    }
    const edgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const edgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
    const handlesMatch =
      edgeSourceInfo.fieldClientId === sourceInfo.fieldClientId &&
      edgeSourceInfo.sectionType === sourceInfo.sectionType &&
      edgeTargetInfo.fieldClientId === targetInfo.fieldClientId &&
      edgeTargetInfo.sectionType === targetInfo.sectionType;
    const hasMatchingControlFlow = nodePairsMatchingDataFlowEdge.some(
      (nodePair) => nodePair.sourceNodeId === edge.source && nodePair.targetNodeId === edge.target
    );
    return handlesMatch && hasMatchingControlFlow;
  });
}

/**
 * Processes edge changes with mirroring logic, performs mirroring for mapping and transition source data.
 */
export function processEdgeChangesWithMirroring(
  edgeChanges: Array<FlowchartEdgeChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): Array<FlowchartEdgeChange> {
  const newEdgeChanges = [];
  const allMirroredEdges: Array<FlowchartEdge> = [];

  const edgeIdsBeingRemoved = new Set(
    edgeChanges.filter((change) => change.type === 'remove').map((change) => change.id)
  );
  const edgesAfterRemovals = draft.staticFlowchart.edgesStaticData.filter(
    (edge) => !edgeIdsBeingRemoved.has(edge.id)
  );

  const edgesAfterChanges = [
    ...edgesAfterRemovals,
    ...edgeChanges.flatMap((change) =>
      change.type === 'add' && change.item != null ? [change.item as FlowchartStaticEdgeData] : []
    ),
  ];

  for (const edgeChange of edgeChanges) {
    if (edgeChange.type === 'remove') {
      // Mirroring changes for removed data flow edges
      const deletedEdge = draft.staticFlowchart.edgesStaticData.find((edge) => edge.id === edgeChange.id);
      if (deletedEdge && deletedEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        // Only mirror DF removals when the original edge's instance still has a CF
        // connection. When the CF edge has been removed from this instance (i.e. the
        // instance is "detached"), DF removals should be local and not cascade to
        // other instances that are still fully connected.
        // Use edgesAfterRemovals so that CF edges being removed in the same batch
        // are properly excluded from the connectivity check.
        const nodesWithData = draft.staticFlowchart.nodesStaticData.map((node) => ({
          id: node.id,
          data: node,
        }));
        const originalInstanceHasControlFlow = findAllDownstreamMappableNodeIds(
          {nodes: nodesWithData, edges: edgesAfterRemovals},
          deletedEdge.source
        ).includes(deletedEdge.target);

        if (originalInstanceHasControlFlow) {
          const edgesBetweenSamePTDVs = extractMatchingEdges(
            draft.staticFlowchart.edgesStaticData,
            deletedEdge.source,
            deletedEdge.target,
            draft.staticFlowchart.nodesStaticData,
            ProcessFlowchartEdgeType.DATA_FLOW
          );
          const edgesBetweenSamePTDVsWithControlFlow = edgesBetweenSamePTDVs.filter((edge) =>
            findAllDownstreamMappableNodeIds(
              {nodes: nodesWithData, edges: edgesAfterRemovals},
              edge.source
            ).includes(edge.target)
          );

          if (edgesBetweenSamePTDVsWithControlFlow.length > 0) {
            const deletedSourceInfo = getFieldInfoFromDataFlowEdgeHandle(deletedEdge.sourceHandle);
            const deletedTargetInfo = getFieldInfoFromDataFlowEdgeHandle(deletedEdge.targetHandle);
            const mirrorEdgesToRemove = edgesBetweenSamePTDVsWithControlFlow
              .filter((edge) => {
                const edgeSourceInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
                const edgeTargetInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);
                return (
                  edgeSourceInfo.fieldClientId === deletedSourceInfo.fieldClientId &&
                  edgeSourceInfo.sectionType === deletedSourceInfo.sectionType &&
                  edgeTargetInfo.fieldClientId === deletedTargetInfo.fieldClientId &&
                  edgeTargetInfo.sectionType === deletedTargetInfo.sectionType
                );
              })
              .map((edge) => edge.id);

            newEdgeChanges.push(
              ...mirrorEdgesToRemove.map((edgeId) => ({type: 'remove' as const, id: edgeId}))
            );
          }
        }
      }
    } else if (edgeChange.type === 'add') {
      // Mirroring changes for added data flow edges
      const newEdge = edgeChange.item;
      if (newEdge && newEdge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
        const nodePairsToMirrorDataFlow = extractMatchingNodePairs(
          draft.staticFlowchart.edgesStaticData,
          newEdge.source,
          newEdge.target,
          draft.staticFlowchart.nodesStaticData
        );

        if (nodePairsToMirrorDataFlow.length > 0) {
          const mirrorEdges = compact(
            nodePairsToMirrorDataFlow.map((nodePair) => {
              const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.sourceHandle!);
              const targetInfo = getFieldInfoFromDataFlowEdgeHandle(newEdge.targetHandle!);
              const mirrorEdge: FlowchartEdge = {
                source: nodePair.sourceNodeId,
                target: nodePair.targetNodeId,
                id: uuidv4(),
                sourceHandle: makeDataFlowEdgeHandle({
                  nodeId: nodePair.sourceNodeId,
                  fieldClientId: sourceInfo.fieldClientId,
                  sectionType: sourceInfo.sectionType,
                  handleType: 'source',
                }),
                targetHandle: makeDataFlowEdgeHandle({
                  nodeId: nodePair.targetNodeId,
                  fieldClientId: targetInfo.fieldClientId,
                  sectionType: targetInfo.sectionType,
                  handleType: 'target',
                }),
                type: ProcessFlowchartEdgeType.DATA_FLOW,
              };
              // Filter out duplicate edges
              if (isNewEdgeDuplicate(draft.staticFlowchart.edgesStaticData, mirrorEdge)) {
                return null;
              }
              return mirrorEdge;
            })
          );

          newEdgeChanges.push(...mirrorEdges.map((mirrorEdge) => ({type: 'add' as const, item: mirrorEdge})));
        }
      } else if (newEdge && newEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
        // Resolve through routers to get actual task node pairs
        const resolvedPairs = getTaskSchemaNodePairsForEdge(
          newEdge,
          draft.staticFlowchart.nodesStaticData,
          edgesAfterChanges
        );

        for (const {sourceNode: resolvedSource, targetNode: resolvedTarget} of resolvedPairs) {
          // Mirroring data flow edges for new control flow edges
          const existingDataFlowEdgesBetweenNodes = draft.staticFlowchart.edgesStaticData.filter(
            (edge) =>
              edge.source === resolvedSource.id &&
              edge.target === resolvedTarget.id &&
              edge.type === ProcessFlowchartEdgeType.DATA_FLOW
          );

          // Add data flow edges that are on other nodes with matching PTDVs
          const dataFlowEdgesToMirrorControlFlow = extractMatchingEdges(
            draft.staticFlowchart.edgesStaticData,
            resolvedSource.id,
            resolvedTarget.id,
            draft.staticFlowchart.nodesStaticData,
            ProcessFlowchartEdgeType.DATA_FLOW
          );

          const sourceHandleAndTargetPairsToMirror = new Map<
            string,
            {sourceHandle: string; targetHandle: string}
          >();
          for (const dataFlowEdge of dataFlowEdgesToMirrorControlFlow) {
            const key = `${dataFlowEdge.sourceHandle}:${dataFlowEdge.targetHandle}`;
            sourceHandleAndTargetPairsToMirror.set(key, {
              sourceHandle: dataFlowEdge.sourceHandle,
              targetHandle: dataFlowEdge.targetHandle,
            });
          }

          const mirrorEdgesToAdd: Array<FlowchartEdge> = [];
          for (const {sourceHandle, targetHandle} of sourceHandleAndTargetPairsToMirror.values()) {
            const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(sourceHandle);
            const targetInfo = getFieldInfoFromDataFlowEdgeHandle(targetHandle);
            const mirrorEdge = {
              id: uuidv4(),
              type: ProcessFlowchartEdgeType.DATA_FLOW,
              source: resolvedSource.id,
              target: resolvedTarget.id,
              sourceHandle: makeDataFlowEdgeHandle({
                nodeId: resolvedSource.id,
                fieldClientId: sourceInfo.fieldClientId,
                sectionType: sourceInfo.sectionType,
                handleType: 'source',
              }),
              targetHandle: makeDataFlowEdgeHandle({
                nodeId: resolvedTarget.id,
                fieldClientId: targetInfo.fieldClientId,
                sectionType: targetInfo.sectionType,
                handleType: 'target',
              }),
            };
            // Filter out duplicate edges
            if (
              !isNewEdgeDuplicate(draft.staticFlowchart.edgesStaticData, mirrorEdge) &&
              !isNewEdgeDuplicate(mirrorEdgesToAdd, mirrorEdge) &&
              !isNewEdgeDuplicate(allMirroredEdges, mirrorEdge)
            ) {
              mirrorEdgesToAdd.push(mirrorEdge);
            }
          }

          if (mirrorEdgesToAdd.length > 0) {
            allMirroredEdges.push(...mirrorEdgesToAdd);
            newEdgeChanges.push(...mirrorEdgesToAdd.map((edge) => ({type: 'add' as const, item: edge})));
          }

          // Mirror existing data flow edges for the new control flow edge
          for (const dataFlowEdge of existingDataFlowEdgesBetweenNodes) {
            const additionalMirrorEdges = processDataFlowEdgeMirroring(
              {
                source: resolvedSource.id,
                target: resolvedTarget.id,
                sourceHandle: dataFlowEdge.sourceHandle,
                targetHandle: dataFlowEdge.targetHandle,
              },
              draft.staticFlowchart.edgesStaticData,
              draft.staticFlowchart.nodesStaticData
            );
            newEdgeChanges.push(...additionalMirrorEdges.map((edge) => ({type: 'add' as const, item: edge})));
          }
        }
      }
    }
  }

  const numEdgesAdded = newEdgeChanges.filter((edge) => edge.type === 'add').length;
  const numEdgesRemoved = newEdgeChanges.filter((edge) => edge.type === 'remove').length;
  const analyticsMetadata = {
    actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
    flowchartType: draft.metadata.flowchartTypeName,
  };
  if (numEdgesAdded > 0) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.DATA_FLOW_EDGE_ADDED_VIA_MIRRORING, {
      ...analyticsMetadata,
      numEdgesMirrored: numEdgesAdded,
    });
  }
  if (numEdgesRemoved > 0) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.DATA_FLOW_EDGE_REMOVED_VIA_MIRRORING, {
      ...analyticsMetadata,
      numEdgesMirrored: numEdgesRemoved,
    });
  }

  return [...edgeChanges, ...newEdgeChanges];
}

/**
 * Helper function to process data flow edge mirroring
 */
function processDataFlowEdgeMirroring(
  connection: Connection,
  edges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>
): Array<FlowchartEdge> {
  const nodePairsToMirror = extractMatchingNodePairs(edges, connection.source!, connection.target!, nodes);

  if (nodePairsToMirror.length === 0) {
    return [];
  }

  const mirrorEdges = compact(
    nodePairsToMirror.map((nodePair) => {
      assert(connection.sourceHandle != null, 'Source handle must be defined');
      assert(connection.targetHandle != null, 'Target handle must be defined');
      const sourceInfo = getFieldInfoFromDataFlowEdgeHandle(connection.sourceHandle);
      const targetInfo = getFieldInfoFromDataFlowEdgeHandle(connection.targetHandle);
      const newEdge = {
        source: nodePair.sourceNodeId,
        target: nodePair.targetNodeId,
        id: uuidv4(),
        sourceHandle: makeDataFlowEdgeHandle({
          nodeId: nodePair.sourceNodeId,
          fieldClientId: sourceInfo.fieldClientId,
          sectionType: sourceInfo.sectionType,
          handleType: 'source',
        }),
        targetHandle: makeDataFlowEdgeHandle({
          nodeId: nodePair.targetNodeId,
          fieldClientId: targetInfo.fieldClientId,
          sectionType: targetInfo.sectionType,
          handleType: 'target',
        }),
        type: ProcessFlowchartEdgeType.DATA_FLOW,
      };
      // Filter out duplicate edges
      if (isNewEdgeDuplicate(edges, newEdge)) {
        return null;
      }
      return newEdge;
    })
  );

  return mirrorEdges;
}
